// Use the local backend API, which proxies to Dailey Core with tenant context
const DEFAULT_API_URL = process.env.REACT_APP_API_URL || 'http://100.101.197.63:5992/api';

export default class AppAuth {
  constructor({
    apiUrl = DEFAULT_API_URL,
    tokenKey = 'token',
    refreshTokenKey = 'refresh_token'
  } = {}) {
    // Remove /api suffix if present since we add it in the methods
    this.apiUrl = apiUrl.replace(/\/api\/?$/, '');
    this.tokenKey = tokenKey;
    this.refreshTokenKey = refreshTokenKey;
    this.token = localStorage.getItem(this.tokenKey) || localStorage.getItem('auth_token');
    this.refreshToken = localStorage.getItem(this.refreshTokenKey);
  }

  get headers() {
    return {
      'Content-Type': 'application/json'
    };
  }

  storeTokens(accessToken, refreshToken) {
    if (accessToken) {
      this.token = accessToken;
      localStorage.setItem(this.tokenKey, accessToken);
      localStorage.setItem('auth_token', accessToken);
    }
    if (refreshToken) {
      this.refreshToken = refreshToken;
      localStorage.setItem(this.refreshTokenKey, refreshToken);
    }
  }

  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('auth_token');
    localStorage.removeItem(this.refreshTokenKey);
  }

  async login(identifier, password) {
    const response = await fetch(`${this.apiUrl}/api/login`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({
        email: identifier,
        username: identifier,
        password
      })
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Login failed');
    }

    if (data.token) {
      this.storeTokens(data.token, data.refreshToken);
    }

    return data;
  }

  async validateToken() {
    if (!this.token) {
      return null;
    }

    try {
      // Call the backend's validate endpoint which proxies to Core with tenant context
      const response = await fetch(`${this.apiUrl}/api/validate-token`, {
        method: 'GET',
        headers: {
          ...this.headers,
          Authorization: `Bearer ${this.token}`
        }
      });

      // Only clear tokens for authentication errors (401), not server errors (500)
      if (response.status === 401) {
        this.clearTokens();
        return null;
      }

      // For other errors (500, 503, etc.), return null but keep tokens for retry
      if (!response.ok) {
        console.warn(`Token validation failed with ${response.status}, keeping tokens for retry`);
        return null;
      }

      const data = await response.json();
      if (data.valid) {
        return data;
      }

      // Invalid token response - clear tokens
      this.clearTokens();
      return null;
    } catch (error) {
      // Network errors - keep tokens for retry
      console.warn('Token validation network error, keeping tokens for retry:', error.message);
      return null;
    }
  }

  async logout() {
    if (!this.token) {
      this.clearTokens();
      return;
    }

    try {
      await fetch(`${this.apiUrl}/api/logout`, {
        method: 'POST',
        headers: {
          ...this.headers,
          Authorization: `Bearer ${this.token}`
        }
      });
    } catch (error) {
      // Ignore logout network failures, still clear tokens
      console.warn('Logout request failed:', error);
    } finally {
      this.clearTokens();
    }
  }

  isAuthenticated() {
    return !!this.token;
  }
}
